import {
  AnimatePresence,
  motion,
  type useMotionValue,
  useTransform
} from 'framer-motion';
import styled from 'styled-components';

import * as Cell from '@/components/code-illustrations/cells';
import { mediumSpring, tightSpring } from '@/constants/animation';
import { highlightSyntax } from '@/utils/syntax-highlighting';

import CellPresence from './CellPresence';
import GraphCell from './GraphCell';
import LoadingEllipses from './LoadingEllipses';
import Table from './Table';

const SQL_QUERY = highlightSyntax(
  `SELECT
    CHURN_TIME,
    AVG(PCT_COHORT_RETAINED) AS retention_agg
FROM
    cohorts_retention
GROUP BY
    CHURN_TIME`,
  'sql'
);

const PY_CODE = highlightSyntax(
  `retention_agg["MOVING_AVG_RETENTION"] = (
    retention_agg["RETENTION_AGG"].rolling(window=4, min_periods=1).mean()
)`,
  'py'
);

const OverflowHidden = styled.div`
  border-radius: inherit;
  overflow: hidden;
`;

const Pile = styled(motion.div)`
  display: grid;
  align-items: start;

  > * {
    grid-area: 1/1;
  }
`;

const OverflowContainer = styled.div`
  position: relative;
  max-height: 265px;
  display: flex;
  justify-content: flex-end;
  flex-direction: column;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MOBILE}) {
    max-height: 240px;
  }
`;

const OverflowMask = styled.div`
  display: flex;
  flex-direction: column;
  gap: 52px;
  padding: 30px 0;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MOBILE}) {
    padding: 90px 0;
  }
`;

const FinalCellPresence = styled(CellPresence)`
  max-height: 300px;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MOBILE}) {
    max-height: 140px;
  }
`;

export default function Cells({
  state,
  illustrationState
}: {
  illustrationState: number;
  state: ReturnType<typeof useMotionValue<number>>;
}) {
  const sqlTypingProgress = useTransform(state, [3, 4], [0, 1]);
  const pyTypingProgress = useTransform(state, [6, 7], [0, 1]);

  return (
    <OverflowContainer>
      <OverflowMask>
        <Cell.Cell
          data-active={0 < illustrationState && illustrationState < 6}
          layout
          transition={mediumSpring}>
          <Cell.RunButton layout transition={mediumSpring} />
          <Cell.Label layout transition={mediumSpring}>
            Retention rolled
          </Cell.Label>
          <OverflowHidden>
            <Pile
              layout
              style={{ originX: 0.5, originY: 1 }}
              transition={{ duration: 0 }}>
              {(function (s: number) {
                switch (s) {
                  case 2:
                    return (
                      <motion.div
                        key="thinking"
                        animate={{ opacity: 1, filter: 'blur(0px)' }}
                        exit={{ opacity: 0, filter: 'blur(5px)' }}
                        initial={{ opacity: 1, filter: 'blur(0px)' }}
                        layout
                        transition={{
                          ease: 'linear',
                          duration: 0.1,
                          layout: tightSpring
                        }}>
                        <Cell.Code>
                          <div>
                            Thinking
                            <LoadingEllipses />
                          </div>
                        </Cell.Code>
                      </motion.div>
                    );

                  case 3:
                    return (
                      <motion.div
                        key="sql"
                        animate={{ opacity: 1, filter: 'blur(0px)' }}
                        exit={{ opacity: 0, filter: 'blur(5px)' }}
                        initial={{ opacity: 0, filter: 'blur(5px)' }}
                        style={{ originX: 0.5, originY: 0 }}
                        transition={{
                          ease: 'linear',
                          duration: 0.2
                        }}>
                        <Cell.FormattedCode
                          language="sql"
                          preformattedCode={SQL_QUERY}
                          typingProgress={sqlTypingProgress}
                        />
                      </motion.div>
                    );

                  case 4:
                  default:
                    return (
                      <div>
                        <Cell.FormattedCode preformattedCode={SQL_QUERY} />
                        {s >= 5 && <Table />}
                        {/* {s >= 4 && <Placeholder>TABLE</Placeholder>} */}
                      </div>
                    );
                }
              })(illustrationState)}
            </Pile>
          </OverflowHidden>
          <Cell.Output layout transition={mediumSpring}>
            retention_agg
          </Cell.Output>
        </Cell.Cell>

        <AnimatePresence>
          {illustrationState >= 6 && (
            <CellPresence key="python">
              <Cell.Cell
                data-active={6 <= illustrationState && illustrationState <= 7}>
                <Cell.RunButton />
                <Cell.Label>Calculate Moving Average</Cell.Label>
                <Cell.FormattedCode
                  language="py"
                  preformattedCode={PY_CODE}
                  typingProgress={pyTypingProgress}
                />

                <Cell.Output>retention_agg</Cell.Output>
              </Cell.Cell>
            </CellPresence>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {illustrationState >= 9 && (
            <FinalCellPresence key="graph">
              <GraphCell
                data-active={7 <= illustrationState && illustrationState <= 9}
              />
            </FinalCellPresence>
          )}
        </AnimatePresence>
      </OverflowMask>
    </OverflowContainer>
  );
}
